import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  journalMonthEntries,
  journalMonthLabel,
  journalMonthShortLabel,
  journalTimeline,
} from '@/utils/journalFeed';
import Reveal from '@/components/base/Reveal';

function TradeJournalPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const scrollByPage = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <section
      id="trade-journal"
      className="w-full bg-background-100 border-y border-background-200 px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[34px] leading-[1.08] sm:text-[44px] lg:text-[50px]">
            Every Trade Leaves a Record.
          </h2>
          <p className="mt-4 text-[15px] text-foreground-600 max-w-[560px]">
            Explore the {journalMonthLabel} XAUUSD entries behind the system — the same ledger as
            the full trade journal.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 lg:gap-12">
          {/* timeline */}
          <Reveal>
            <div className="rounded-lg border border-background-300 bg-background-50 px-5 py-6">
              <div className="flex items-center justify-between gap-3 mb-5">
                <p className="text-[10px] font-data tracking-[0.22em] text-foreground-400">
                  RECENT SEQUENCE
                </p>
                <span className="text-[10px] font-data tracking-[0.14em] text-foreground-400 whitespace-nowrap">
                  {journalMonthShortLabel}
                </span>
              </div>
              <div className="max-h-[460px] overflow-y-auto journal-scroll pr-2">
                <div className="relative">
                  <div className="absolute left-[5px] top-2 bottom-2 w-px bg-background-300"></div>
                  <div className="flex flex-col gap-6">
                    {journalTimeline.map((t, idx) => (
                      <div key={`${t.date}-${idx}`} className="relative pl-6">
                        <span
                          className={`absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full border-2 ${
                            t.profit
                              ? 'border-profit-500 bg-background-50'
                              : 'border-loss-500 bg-background-50'
                          }`}
                        ></span>
                        <p className="font-data text-[11px] tracking-wider text-foreground-500">
                          {t.date}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-[12px] text-foreground-600">XAUUSD</span>
                          <span className="text-[10px] font-data tracking-wider text-foreground-400">
                            {t.side}
                          </span>
                          <span
                            className={`font-data text-[13px] font-semibold ${
                              t.profit ? 'text-profit-600' : 'text-loss-500'
                            }`}
                          >
                            {t.result}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* cards */}
          <div className="min-w-0">
            <div className="relative">
              <div
                ref={scrollRef}
                className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {journalMonthEntries.map((trade, idx) => (
                  <Reveal
                    key={`${trade.date}-${idx}`}
                    delay={Math.min(idx, 5) * 90}
                    className="snap-start shrink-0 basis-full sm:basis-[calc((100%_-_1.25rem)/2)] lg:basis-[calc((100%_-_2.5rem)/3)]"
                  >
                    <article className="rounded-lg border border-background-300 bg-background-50 p-5 h-full hover:-translate-y-1 hover:border-primary-300 transition-all duration-300">
                      <div className="flex items-center justify-between">
                        <span className="font-data text-[13px] font-semibold text-foreground-950">
                          {trade.instrument}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-md text-[10px] font-data tracking-wider ${
                            trade.side === 'BUY'
                              ? 'bg-profit-500/10 text-profit-600'
                              : 'bg-loss-500/10 text-loss-500'
                          }`}
                        >
                          {trade.side}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] font-data tracking-wider text-foreground-400">
                        {trade.date}
                      </p>

                      <div className="mt-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-foreground-500">Entry</span>
                          <span className="font-data text-[13px] text-foreground-900">
                            {trade.entry}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-foreground-500">Exit</span>
                          <span className="font-data text-[13px] text-foreground-900">
                            {trade.exit}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-foreground-500">Volume</span>
                          <span className="font-data text-[13px] text-foreground-900">
                            {trade.volume}
                          </span>
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between">
                        <span className="text-[12px] text-foreground-500">Result</span>
                        <span
                          className={`font-data text-[17px] font-semibold ${
                            trade.profit ? 'text-profit-600' : 'text-loss-500'
                          }`}
                        >
                          {trade.result}
                        </span>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>

              {/* edge fades: only shown while there is more to scroll that way */}
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background-100 to-transparent transition-opacity duration-300 ${
                  canLeft ? 'opacity-100' : 'opacity-0'
                }`}
              ></div>
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background-100 to-transparent transition-opacity duration-300 ${
                  canRight ? 'opacity-100' : 'opacity-0'
                }`}
              ></div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-4">
              <Link
                to="/trade-journal"
                className="inline-flex items-center gap-2 text-[14px] font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                View Full Trade Journal
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => scrollByPage(-1)}
                  disabled={!canLeft}
                  aria-label="Previous trades"
                  className="w-9 h-9 rounded-md border border-background-300 bg-background-50 flex items-center justify-center text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:border-background-300 disabled:hover:text-foreground-700"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <button
                  type="button"
                  onClick={() => scrollByPage(1)}
                  disabled={!canRight}
                  aria-label="Next trades"
                  className="w-9 h-9 rounded-md border border-background-300 bg-background-50 flex items-center justify-center text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:border-background-300 disabled:hover:text-foreground-700"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TradeJournalPreview;